<template>
  <div
    class="search-page"
    :class="{
      'search-page--notes': isNotesSource,
      'search-page--mocks': isMocksSource,
    }"
  >
    <HeaderMain v-if="isMocksSource" @back="goBack" />
    <NotesHeader v-else-if="isNotesSource" @back="goBack" />
    <QuestionHeader
      v-else
      @back="goBack"
      :flagcounts="0"
      :correctcount="0"
      :incorrectcount="0"
      :percentage="0"
    />

    <div
      class="container search-shell search-shell--docked"
      :class="{
        'search-shell--split': searchDocked && selectedHit,
      }"
    >
      <aside class="search-sidebar">
        <div
          class="search-sidebar-inner"
          :class="{ 'search-sidebar-inner--split': searchDocked && selectedHit }"
        >
          <div class="search-controls-box">
            <div class="search-input-row">
              <button
                v-if="searchDocked && selectedHit"
                type="button"
                class="search-back-to-results"
                aria-label="Back to results"
                @click="clearSelection"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
              <input
                ref="searchInput"
                v-model="query"
                type="search"
                placeholder="Search..."
                @keyup.enter="runSearch"
              />
              <button type="button" class="search-go-btn" @click="runSearch" aria-label="Search">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" :stroke="searchAccentColor" stroke-width="2.5">
                  <circle cx="13" cy="11" r="8" />
                  <line x1="3" y1="21" x2="7.35" y2="16.65" />
                </svg>
              </button>
            </div>

            <div class="search-category-checks">
              <label
                v-for="cat in categoryOptions"
                :key="cat.key"
                class="search-check-row"
                :class="`search-check-row--${cat.key}`"
              >
                <input type="checkbox" v-model="cat.checked" />
                <span class="search-check-box" aria-hidden="true"></span>
                <span>{{ cat.label }}</span>
              </label>
            </div>
          </div>

          <div v-if="!searchDocked && loading" class="search-centered-status">Searching…</div>
          <div v-else-if="!searchDocked && searchError" class="search-centered-status search-centered-status--error">
            {{ searchError }}
          </div>

          <div v-if="searchDocked && selectedHit" class="search-hits-panel">
            <div v-if="loading" class="search-state search-state--compact">Searching…</div>
            <div v-else-if="!hasAnyResults" class="search-state search-state--compact">No results found.</div>
            <div v-else class="search-hits-scroll">
              <div
                v-for="block in visibleResultBlocks"
                :key="'hit-' + block.key"
                class="search-result-block"
                :style="{ borderColor: block.theme.primary }"
              >
                <div class="search-result-head" :style="{ background: block.theme.primary }">
                  <h4>{{ block.label }} ({{ block.count }})</h4>
                </div>
                <div class="search-result-grid">
                  <button
                    v-for="item in block.items"
                    :key="'hit-' + block.key + '-' + item.index"
                    type="button"
                    class="search-result-num"
                    :class="[
                      `search-result-num--${block.key}`,
                      { 'search-result-num--active': isHitSelected(block.key, item) },
                    ]"
                    :style="searchResultItemStyle(block.key, item, isHitSelected(block.key, item))"
                    @click="selectHit(block.key, item)"
                  >
                    {{ item.index }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </aside>

      <main v-if="searchDocked" class="search-main search-panel">
        <!-- Results list (before a hit is selected) -->
        <div v-if="!selectedHit" class="search-results-stack">
          <div v-if="loading" class="search-state">Searching…</div>
          <div v-else-if="searchError" class="search-state search-state--error">{{ searchError }}</div>
          <div v-else-if="hasSearched && !hasAnyResults" class="search-state">No results found.</div>
          <div v-else class="search-results-list">
            <div
              v-for="block in visibleResultBlocks"
              :key="block.key"
              class="search-result-block"
              :style="{ borderColor: block.theme.primary }"
            >
              <div class="search-result-head" :style="{ background: block.theme.primary }">
                <h4>Found {{ block.count }} result{{ block.count === 1 ? '' : 's' }} for '{{ lastQuery }}' in {{ block.label }}</h4>
              </div>
              <div class="search-result-grid">
                <button
                  v-for="item in block.items"
                  :key="block.key + '-' + item.index"
                  type="button"
                  class="search-result-num"
                  :style="searchResultItemStyle(block.key, item, false)"
                  @click="selectHit(block.key, item)"
                >
                  {{ item.index }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Detail view (after a hit is selected) -->
        <div v-else class="search-detail-panel">
          <button type="button" class="search-detail-back" @click="clearSelection">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Back to results
          </button>

          <template v-if="isQuestionDetailView">
            <section class="search-question-breadcrum">
              <div class="search-breadcrumb">
                <span
                  v-for="(label, idx) in searchQuestionBreadcrumbItems"
                  :key="'search-bc-' + idx"
                  class="breadcrumb-segment"
                >
                  <span v-if="idx > 0" class="breadcrumb-arrow">›</span>
                  <span
                    class="breadcrumb-item"
                    :class="{ active: idx === searchQuestionBreadcrumbItems.length - 1 }"
                  >{{ label }}</span>
                </span>
              </div>
            </section>

            <div v-if="questionHits.length" class="search-question-scroller-wrap">
              <div class="search-question-scroller-row">
                <button
                  type="button"
                  class="search-q-scroll-arrow"
                  aria-label="Scroll questions left"
                  @click="scrollSearchQuestions(-1)"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="m15 18-6-6 6-6" />
                  </svg>
                </button>

                <div
                  ref="searchQuestionScroll"
                  class="search-question-slide"
                  :class="{ 'search-question-slide--dragging': searchScrollerDragging }"
                  @mousedown="onSearchScrollerMouseDown($event, 'searchQuestionScroll')"
                >
                  <span
                    v-for="(hit, hitIdx) in questionHits"
                    :key="'sq-' + hit.id + '-' + hit.index"
                    class="search-q-number"
                    :style="searchQuestionScrollerStyle(hit)"
                    @click="onSearchScrollerItemClick(hitIdx)"
                  >
                    <span class="search-q-number-label">{{ hit.index }}</span>
                  </span>
                </div>

                <button
                  type="button"
                  class="search-q-scroll-arrow"
                  aria-label="Scroll questions right"
                  @click="scrollSearchQuestions(1)"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                </button>
              </div>
            </div>
          </template>

          <template v-else-if="isNoteDetailView">
            <section class="search-question-breadcrum">
              <div class="search-breadcrumb search-breadcrumb--notes">
                <span
                  v-for="(label, idx) in searchNoteBreadcrumbItems"
                  :key="'search-note-bc-' + idx"
                  class="breadcrumb-segment"
                >
                  <span v-if="idx > 0" class="breadcrumb-arrow">›</span>
                  <span
                    class="breadcrumb-item"
                    :class="{ active: idx === searchNoteBreadcrumbItems.length - 1 }"
                  >{{ label }}</span>
                </span>
              </div>
            </section>
          </template>

          <template v-else-if="isMockDetailView">
            <div v-if="mockHits.length" class="search-question-scroller-wrap">
              <div class="search-question-scroller-row">
                <button
                  type="button"
                  class="search-q-scroll-arrow"
                  aria-label="Scroll mocks left"
                  @click="scrollSearchMocks(-1)"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="m15 18-6-6 6-6" />
                  </svg>
                </button>

                <div
                  ref="searchMockScroll"
                  class="search-question-slide"
                  :class="{ 'search-question-slide--dragging': searchScrollerDragging }"
                  @mousedown="onSearchScrollerMouseDown($event, 'searchMockScroll')"
                >
                  <span
                    v-for="(hit, hitIdx) in mockHits"
                    :key="'sm-' + hit.id + '-' + hit.index"
                    class="search-q-number"
                    :style="searchMockScrollerStyle(hit)"
                    @click="onSearchMockScrollerItemClick(hitIdx)"
                  >
                    <span class="search-q-number-label">{{ hit.index }}</span>
                  </span>
                </div>

                <button
                  type="button"
                  class="search-q-scroll-arrow"
                  aria-label="Scroll mocks right"
                  @click="scrollSearchMocks(1)"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                </button>
              </div>
            </div>
          </template>

          <div v-if="detailLoading" class="search-state">Loading…</div>
          <div v-else-if="detailError" class="search-state search-state--error">{{ detailError }}</div>
          <div v-else-if="detail" class="search-detail-body">
            <div
              v-if="detail.context_label && detail.category === 'questions' && !isQuestionDetailView"
              class="search-context-banner"
              v-html="highlightPlain(detail.context_label)"
            />

            <h2 v-if="detailTitle" class="search-detail-title" v-html="highlightPlain(detailTitle)"></h2>

            <div v-if="detail.category === 'questions' && detail.question" class="search-question-body">
              <div class="search-q-text" v-html="highlightHtml(detail.question.question_text)"></div>
              <div
                v-for="opt in detail.question.options || []"
                :key="opt.id"
                class="search-q-option"
              >
                <strong>{{ opt.prefix }}.</strong>
                <span v-html="highlightHtml(opt.option_text)"></span>
              </div>

              <div v-if="questionHits.length > 1" class="search-question-nav-arrows">
                <div
                  class="questionoption-leftarrow"
                  :class="{ 'search-nav-disabled': !canGoPreviousQuestionHit }"
                  @click="previousQuestionHit"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="m15 18-6-6 6-6" />
                  </svg>
                </div>
                <div
                  class="questionoption-rightarrow"
                  :class="{ 'search-nav-disabled': !canGoNextQuestionHit }"
                  @click="nextQuestionHit"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                </div>
              </div>

              <button type="button" class="search-open-full" @click="openQuestionInApp">Open question</button>
            </div>

            <div v-else-if="detail.category === 'notes'" class="search-note-body">
              <div
                v-if="detail.content_html"
                class="search-rich-content"
                v-html="highlightHtml(detail.content_html)"
              ></div>

              <div v-if="noteHits.length > 1" class="search-question-nav-arrows">
                <div
                  class="questionoption-leftarrow"
                  :class="{ 'search-nav-disabled': !canGoPreviousNoteHit }"
                  @click="previousNoteHit"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="m15 18-6-6 6-6" />
                  </svg>
                </div>
                <div
                  class="questionoption-rightarrow"
                  :class="{ 'search-nav-disabled': !canGoNextNoteHit }"
                  @click="nextNoteHit"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                </div>
              </div>

              <button type="button" class="search-open-full search-open-full--notes" @click="openNoteInApp">Open note</button>
            </div>

            <div v-else-if="detail.category === 'mocks' && detail.question" class="search-question-body">
              <div class="search-q-text" v-html="highlightHtml(detail.question.title)"></div>
              <div
                v-for="opt in detail.question.options || []"
                :key="opt.id"
                class="search-q-option"
              >
                <strong>{{ opt.prefix }}.</strong>
                <span v-html="highlightHtml(opt.title)"></span>
              </div>

              <div v-if="mockHits.length > 1" class="search-question-nav-arrows">
                <div
                  class="questionoption-leftarrow"
                  :class="{ 'search-nav-disabled': !canGoPreviousMockHit }"
                  @click="previousMockHit"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="m15 18-6-6 6-6" />
                  </svg>
                </div>
                <div
                  class="questionoption-rightarrow"
                  :class="{ 'search-nav-disabled': !canGoNextMockHit }"
                  @click="nextMockHit"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                </div>
              </div>

              <button type="button" class="search-open-full search-open-full--mocks" @click="openMockInApp">Open mock question</button>
            </div>

            <div
              v-else-if="detail.content_html"
              class="search-rich-content"
              v-html="highlightHtml(detail.content_html)"
            ></div>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import QuestionHeader from '@/components/QuestionHeader.vue';
import NotesHeader from '@/components/NotesHeader.vue';
import HeaderMain from '@/components/HeaderMain.vue';
import { get } from './lib/api';
import { highlightHtml, highlightPlain } from './lib/searchHighlight';
import {
  hydrateNoteUserState,
  invalidateNotesPrefsCache,
  isRecordRead,
  notePrefsUi,
  registerNoteRecord,
} from './lib/notesUserPrefs';

const SEARCH_QUESTION_CORRECT_BG = '#9ded6c';
const SEARCH_QUESTION_WRONG_BG = '#ffbabe';
const SEARCH_NOTE_READ_BG = '#9ded6c';

const CATEGORY_META = {
  questions: { label: 'Questions', theme: { primary: '#1A90FF', secondary: '#D7EBFF' } },
  notes: { label: 'Notes', theme: { primary: '#F59C00', secondary: '#fce2b5' } },
  journal: { label: 'Journal', theme: { primary: '#A7A9AC', secondary: '#E8E8E8' } },
  comment: { label: 'Comment', theme: { primary: '#A7A9AC', secondary: '#E8E8E8' } },
  mocks: { label: 'Mocks', theme: { primary: '#ED1C24', secondary: '#ffd6d8' } },
};

function defaultCategories(selectedKeys) {
  const all = [
    { key: 'questions', label: 'Questions', checked: false, icon: '/images/questionmark.png' },
    { key: 'notes', label: 'Notes', checked: false, icon: '/images/file.png' },
    { key: 'journal', label: 'Journal', checked: false, icon: '/images/journal-icon.png' },
    { key: 'comment', label: 'Comment', checked: false, icon: '/images/comments-icon.svg' },
    { key: 'mocks', label: 'Mocks', checked: false, icon: '/images/brain.png' },
  ];
  if (!selectedKeys || !selectedKeys.length) return all;
  const set = new Set(selectedKeys);
  return all.map((c) => ({ ...c, checked: set.has(c.key) }));
}

export default {
  name: 'SearchPage',
  components: { QuestionHeader, NotesHeader, HeaderMain },
  data() {
    return {
      query: '',
      lastQuery: '',
      categoryOptions: defaultCategories(),
      results: {},
      loading: false,
      searchError: '',
      hasSearched: false,
      selectedHit: null,
      detail: null,
      detailLoading: false,
      detailError: '',
      returnPath: '/mlalistselection',
      searchScrollerDragging: false,
      searchScrollerDragMoved: false,
      searchScrollerStartX: 0,
      searchScrollerScrollLeft: 0,
      notesReadRev: 0,
      resultsDismissed: false,
      searchDocked: false,
    };
  },
  computed: {
    notePrefsRevision() {
      void this.notesReadRev;
      return notePrefsUi.rev;
    },
    isNotesSource() {
      if (this.isMocksSource) return false;
      if (this.$route.query.source === 'notes') return true;
      const from = String(this.$route.query.from || this.returnPath || '');
      return from.includes('noteslistselection');
    },
    isMocksSource() {
      if (this.$route.query.source === 'mocks') return true;
      const from = String(this.$route.query.from || this.returnPath || '');
      return from.includes('mocksection') || from.includes('mockselection');
    },
    searchAccentColor() {
      if (this.isMocksSource) return '#ED1C24';
      if (this.isNotesSource) return '#faad1b';
      return '#1A90FF';
    },
    questionScope() {
      const fromRoute = String(this.$route.query.question_scope || '').trim();
      if (fromRoute) return fromRoute;
      if (this.isMocksSource) return 'text';
      if (this.isNotesSource) return 'areas';
      return 'areas';
    },
    activeCategories() {
      return this.categoryOptions.filter((c) => c.checked).map((c) => c.key);
    },
    visibleResultBlocks() {
      return this.activeCategories
        .map((key) => {
          const bucket = this.results[key];
          if (!bucket || !bucket.count) return null;
          const meta = CATEGORY_META[key];
          return {
            key,
            label: meta.label,
            theme: meta.theme,
            count: bucket.count,
            items: bucket.items || [],
          };
        })
        .filter(Boolean);
    },
    hasAnyResults() {
      return this.visibleResultBlocks.length > 0;
    },
    detailTitle() {
      if (!this.detail) return '';
      if (this.detail.category === 'questions' && this.detail.question) {
        const code = this.detail.question.code || '';
        return code ? `${code}` : 'Question';
      }
      if (this.detail.category === 'mocks') {
        const hit = (this.selectedHit && this.selectedHit.item) || {};
        const q = this.detail.question || {};
        return (
          this.detail.title
          || q.number
          || hit.number
          || (q.question_no != null ? String(q.question_no) : '')
          || (hit.question_no != null ? String(hit.question_no) : '')
          || 'Mock question'
        );
      }
      if (this.detail.category === 'notes') {
        const hit = (this.selectedHit && this.selectedHit.item) || {};
        return this.noteResultLabel({
          title: this.detail.title || hit.title,
          number: this.detail.number || hit.number,
        });
      }
      return this.detail.title || '';
    },
    questionHits() {
      const bucket = this.results.questions;
      return (bucket && Array.isArray(bucket.items)) ? bucket.items : [];
    },
    noteHits() {
      const bucket = this.results.notes;
      return (bucket && Array.isArray(bucket.items)) ? bucket.items : [];
    },
    mockHits() {
      const bucket = this.results.mocks;
      return (bucket && Array.isArray(bucket.items)) ? bucket.items : [];
    },
    isQuestionDetailView() {
      return Boolean(this.selectedHit && this.selectedHit.category === 'questions');
    },
    isNoteDetailView() {
      return Boolean(this.selectedHit && this.selectedHit.category === 'notes');
    },
    isMockDetailView() {
      return Boolean(this.selectedHit && this.selectedHit.category === 'mocks');
    },
    selectedQuestionHitIndex() {
      if (!this.selectedHit || this.selectedHit.category !== 'questions') return -1;
      const item = this.selectedHit.item;
      return this.questionHits.findIndex(
        (hit) => String(hit.id) === String(item.id) && String(hit.index) === String(item.index)
      );
    },
    selectedNoteHitIndex() {
      if (!this.selectedHit || this.selectedHit.category !== 'notes') return -1;
      const item = this.selectedHit.item;
      return this.noteHits.findIndex(
        (hit) => String(hit.id) === String(item.id) && String(hit.index) === String(item.index)
      );
    },
    canGoPreviousQuestionHit() {
      return this.selectedQuestionHitIndex > 0;
    },
    canGoNextQuestionHit() {
      return (
        this.selectedQuestionHitIndex >= 0
        && this.selectedQuestionHitIndex < this.questionHits.length - 1
      );
    },
    canGoPreviousNoteHit() {
      return this.selectedNoteHitIndex > 0;
    },
    canGoNextNoteHit() {
      return (
        this.selectedNoteHitIndex >= 0
        && this.selectedNoteHitIndex < this.noteHits.length - 1
      );
    },
    selectedMockHitIndex() {
      if (!this.selectedHit || this.selectedHit.category !== 'mocks') return -1;
      const item = this.selectedHit.item;
      return this.mockHits.findIndex(
        (hit) => String(hit.id) === String(item.id) && String(hit.index) === String(item.index)
      );
    },
    canGoPreviousMockHit() {
      return this.selectedMockHitIndex > 0;
    },
    canGoNextMockHit() {
      return (
        this.selectedMockHitIndex >= 0
        && this.selectedMockHitIndex < this.mockHits.length - 1
      );
    },
    searchQuestionBreadcrumbItems() {
      if (!this.isQuestionDetailView) return [];

      const hit = (this.selectedHit && this.selectedHit.item) || {};
      const q = (this.detail && this.detail.question) || {};
      const detail = this.detail || {};

      if (this.questionScope === 'areas') {
        return this.buildAreasQuestionBreadcrumbItems(hit, q, detail);
      }

      const fromApi = detail.breadcrumb_labels || detail.breadcrumbs || detail.breadcrumb
        || hit.breadcrumb_labels || hit.breadcrumbs || hit.breadcrumb;
      if (Array.isArray(fromApi) && fromApi.length) {
        return fromApi.map((x) => String(x).trim()).filter(Boolean);
      }
      if (typeof fromApi === 'string' && fromApi.trim()) {
        const parts = fromApi.split(/\s*[>›|/]\s*/).map((s) => s.trim()).filter(Boolean);
        if (parts.length > 1) return parts;
      }

      const topic = this.pickSearchTopicLabel(q, hit, detail);
      const src = { ...hit, ...q };

      if (src.presentation_id) {
        return ['MLA content map', 'Presentations and conditions', 'Presentations', topic || 'Question'].filter(Boolean);
      }
      if (src.sublist_id) {
        return ['MLA content map', 'Areas', 'Sub-condition', topic || 'Question'].filter(Boolean);
      }
      if (src.condition_id) {
        return ['MLA content map', 'Areas', 'Conditions', topic || 'Question'].filter(Boolean);
      }
      if (src.subject_id) {
        return ['MLA content map', 'Areas', 'Areas of clinical practice', topic || 'Question'].filter(Boolean);
      }

      if (topic) {
        return ['MLA content map', 'Areas', 'Areas of clinical practice', topic];
      }
      return ['MLA content map', 'Search', this.detailTitle || hit.code || 'Question'].filter(Boolean);
    },
    searchNoteBreadcrumbItems() {
      if (!this.isNoteDetailView) return [];

      const hit = (this.selectedHit && this.selectedHit.item) || {};
      const detail = this.detail || {};

      const fromApi = detail.breadcrumb_labels || detail.breadcrumbs || detail.breadcrumb
        || hit.breadcrumb_labels || hit.breadcrumbs || hit.breadcrumb;
      if (Array.isArray(fromApi) && fromApi.length) {
        return fromApi.map((x) => String(x).trim()).filter(Boolean);
      }
      if (typeof fromApi === 'string' && fromApi.trim()) {
        const parts = fromApi.split(/\s*[>›|/]\s*/).map((s) => s.trim()).filter(Boolean);
        if (parts.length > 1) return parts;
      }

      const leaf = String(
        detail.title
        || hit.title
        || hit.context_label
        || detail.context_label
        || 'Note'
      ).trim();

      const parent = this.pickSearchTopicLabel({}, hit, detail);
      const section = detail.section_title || hit.section_title || detail.parent_title || hit.parent_title || null;
      const viewMode = detail.view_mode_label || hit.view_mode_label || 'Areas';

      const items = ['MLA content map', viewMode];
      if (section && section !== leaf && section !== parent) items.push(section);
      if (parent && parent !== leaf) items.push(parent);
      items.push(leaf);
      return items.filter(Boolean);
    },
  },
  watch: {
    activeCategories: {
      handler() {
        if (this.lastQuery) {
          this.runSearch({ preserveSelection: !!this.selectedHit });
        }
      },
      deep: true,
    },
    selectedQuestionHitIndex(idx) {
      if (idx >= 0) this.scheduleCenterSearchQuestionScroller();
    },
    isQuestionDetailView(active) {
      if (active) this.scheduleCenterSearchQuestionScroller();
    },
    selectedMockHitIndex(idx) {
      if (idx >= 0) this.scheduleCenterSearchMockScroller();
    },
    isMockDetailView(active) {
      if (active) this.scheduleCenterSearchMockScroller();
    },
  },
  created() {
    if (this.$route.query.from) {
      this.returnPath = this.$route.query.from;
    } else if (this.$route.query.source === 'mocks') {
      this.returnPath = '/mocksection';
    } else if (this.$route.query.source === 'notes') {
      this.returnPath = '/noteslistselection';
    }

    const cats = String(this.$route.query.categories || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (cats.length) {
      this.categoryOptions = defaultCategories(cats);
    }

    if (this.$route.query.q) {
      this.query = this.$route.query.q;
      this.runSearch();
    } else {
      this.$nextTick(() => {
        const input = this.$refs.searchInput;
        if (input && typeof input.focus === 'function') input.focus();
      });
    }

    hydrateNoteUserState();
  },
  mounted() {
    this._notesPrefsStorage = (ev) => {
      if (ev && ev.key && String(ev.key).indexOf('notesUserPrefs_') === 0) {
        invalidateNotesPrefsCache();
        this.notesReadRev += 1;
      }
    };
    window.addEventListener('storage', this._notesPrefsStorage);
  },
  activated() {
    this.notesReadRev += 1;
  },
  beforeDestroy() {
    if (this._centerSearchScrollerTimer) {
      clearTimeout(this._centerSearchScrollerTimer);
      this._centerSearchScrollerTimer = null;
    }
    if (this._centerSearchMockScrollerTimer) {
      clearTimeout(this._centerSearchMockScrollerTimer);
      this._centerSearchMockScrollerTimer = null;
    }
    this.teardownSearchScrollerDrag();
    if (this._notesPrefsStorage) {
      window.removeEventListener('storage', this._notesPrefsStorage);
    }
  },
  methods: {
    isHitSelected(category, item) {
      if (!this.selectedHit) return false;
      return (
        this.selectedHit.category === category
        && String(this.selectedHit.item.id) === String(item.id)
        && String(this.selectedHit.item.index) === String(item.index)
      );
    },

    noteResultLabel(item) {
      if (!item) return 'Note';
      const title = String(item.title || '').trim();
      const code = String(item.number || '').trim();
      if (title && code) return `${title} - ${code}`;
      return title || code || 'Note';
    },

    registerNoteRecordsFromResults() {
      const bucket = this.results.notes;
      if (!bucket || !Array.isArray(bucket.items)) return;
      bucket.items.forEach((item) => {
        const type = item.record_type === 'subnotes' ? 'subnotes' : 'notes';
        registerNoteRecord(type, item.id, item.number);
      });
    },

    questionHitStatus(item) {
      const score = item && item.score;
      if (!score || score.correct == null) return 'unattempted';
      return Number(score.correct) === 1 ? 'correct' : 'wrong';
    },

    /** Mocks search only includes attempted questions (same green/pink as questions). */
    isAttemptedHit(item) {
      return this.questionHitStatus(item) !== 'unattempted';
    },

    normalizeSearchResults(raw) {
      const results = { ...(raw || {}) };
      const mocks = results.mocks;
      if (mocks && Array.isArray(mocks.items)) {
        const attempted = mocks.items.filter((item) => this.isAttemptedHit(item));
        results.mocks = {
          ...mocks,
          count: attempted.length,
          items: attempted.map((item, i) => ({ ...item, index: i + 1 })),
        };
      }
      return results;
    },

    noteHitIsRead(item) {
      void this.notePrefsRevision;
      if (!item || item.id == null) return false;
      const type = item.record_type === 'subnotes' ? 'subnotes' : 'notes';
      return isRecordRead(this.$store, type, item.id, item.number);
    },

    searchResultItemStyle(category, item, isActive) {
      const meta = CATEGORY_META[category];
      const defaultBg = (item && item.source_theme && item.source_theme.secondary)
        || (meta && meta.theme.secondary)
        || '#E8E8E8';
      let background = defaultBg;
      let border = '2px solid transparent';

      if (category === 'questions') {
        const status = this.questionHitStatus(item);
        if (status === 'correct') background = SEARCH_QUESTION_CORRECT_BG;
        else if (status === 'wrong') background = SEARCH_QUESTION_WRONG_BG;
        else background = CATEGORY_META.questions.theme.secondary;

        if (isActive) {
          if (status === 'correct') border = '2px solid #2e7d32';
          else if (status === 'wrong') border = '2px solid #ED1C24';
          else border = '2px solid #1A90FF';
        }
      } else if (category === 'notes') {
        if (this.noteHitIsRead(item)) background = SEARCH_NOTE_READ_BG;
        else background = CATEGORY_META.notes.theme.secondary;

        if (isActive) {
          border = this.noteHitIsRead(item) ? '2px solid #2e7d32' : '2px solid #f59c00';
        }
      } else if (category === 'mocks') {
        // Attempted only: green = correct, pink = wrong (same as questions)
        const status = this.questionHitStatus(item);
        if (status === 'correct') background = SEARCH_QUESTION_CORRECT_BG;
        else if (status === 'wrong') background = SEARCH_QUESTION_WRONG_BG;
        else background = CATEGORY_META.mocks.theme.secondary;

        if (isActive) {
          if (status === 'correct') border = '2px solid #2e7d32';
          else if (status === 'wrong') border = '2px solid #ED1C24';
          else border = '2px solid #ED1C24';
        }
      }

      return { background, border };
    },

    searchQuestionScrollerStyle(hit) {
      const isActive = this.isHitSelected('questions', hit);
      const status = this.questionHitStatus(hit);
      let background = CATEGORY_META.questions.theme.secondary;
      if (status === 'correct') background = SEARCH_QUESTION_CORRECT_BG;
      else if (status === 'wrong') background = SEARCH_QUESTION_WRONG_BG;

      let border = '2px solid transparent';
      if (isActive) {
        if (status === 'correct') border = '2px solid #2e7d32';
        else if (status === 'wrong') border = '2px solid #ED1C24';
        else border = '2px solid #949393';
      }

      const style = { background, border };
      if (isActive) {
        style.height = '72px';
        style.fontWeight = '700';
        style.fontSize = '16px';
      }
      return style;
    },

    searchMockScrollerStyle(hit) {
      const isActive = this.isHitSelected('mocks', hit);
      const status = this.questionHitStatus(hit);
      let background = CATEGORY_META.mocks.theme.secondary;
      if (status === 'correct') background = SEARCH_QUESTION_CORRECT_BG;
      else if (status === 'wrong') background = SEARCH_QUESTION_WRONG_BG;

      let border = '2px solid transparent';
      if (isActive) {
        if (status === 'correct') border = '2px solid #2e7d32';
        else if (status === 'wrong') border = '2px solid #ED1C24';
        else border = '2px solid #ED1C24';
      }

      const style = { background, border };
      if (isActive) {
        style.height = '72px';
        style.fontWeight = '700';
        style.fontSize = '16px';
      }
      return style;
    },

    highlightHtml(html) {
      return highlightHtml(html, this.lastQuery || this.query);
    },

    highlightPlain(text) {
      return highlightPlain(text, this.lastQuery || this.query);
    },

    goBack() {
      this.$router.push(this.returnPath);
    },

    clearSelection() {
      this.selectedHit = null;
      this.detail = null;
      this.detailError = '';
      this.resultsDismissed = true;
    },

    autoSelectFirstHit() {
      for (const key of this.activeCategories) {
        const bucket = this.results[key];
        const items = bucket && bucket.items;
        if (items && items.length) {
          return this.selectHit(key, items[0]);
        }
      }
      return Promise.resolve();
    },

    syncSearchQueryToRoute(q) {
      const nextQuery = {
        ...this.$route.query,
        q,
        categories: this.activeCategories.join(','),
        question_scope: this.questionScope,
      };
      this.$router.replace({ path: this.$route.path, query: nextQuery }).catch(() => {});
    },

    async runSearch(opts = {}) {
      const q = (this.query || '').trim();
      if (!q) return;
      if (!this.activeCategories.length) {
        this.searchError = 'Select at least one category.';
        return;
      }

      this.loading = true;
      this.searchError = '';
      this.hasSearched = true;
      const queryChanged = q !== this.lastQuery;
      if (queryChanged) {
        this.resultsDismissed = false;
      }
      if (!opts.preserveSelection) {
        this.selectedHit = null;
        this.detail = null;
      }

      try {
        const res = await get('/search', {
          q,
          question_scope: this.questionScope,
          categories: this.activeCategories,
        });
        this.lastQuery = res.data.query || q;
        this.results = this.normalizeSearchResults(res.data.results || {});
        this.registerNoteRecordsFromResults();
        this.syncSearchQueryToRoute(this.lastQuery);

        const shouldAutoOpen = !this.resultsDismissed
          && (!opts.preserveSelection || !this.selectedHit);
        if (shouldAutoOpen) {
          await this.autoSelectFirstHit();
        }

        this.searchDocked = true;
      } catch (e) {
        this.results = {};
        this.searchError = 'Search failed. Please try again.';
      } finally {
        this.loading = false;
      }
    },

    async selectHit(category, item) {
      this.resultsDismissed = false;
      this.selectedHit = { category, item };
      this.detailLoading = true;
      this.detailError = '';
      this.detail = null;

      if (category === 'questions') {
        this.scheduleCenterSearchQuestionScroller();
      } else if (category === 'mocks') {
        this.scheduleCenterSearchMockScroller();
      }

      try {
        const params = {
          category,
          id: item.id,
          record_type: item.record_type || undefined,
          source_theme: item.source_theme?.key || undefined,
          question_scope: category === 'questions' ? this.questionScope : undefined,
        };
        const res = await get('/search/detail', params);
        const data = res.data.data || {};
        this.detail = {
          ...data,
          category,
          context_label: item.context_label || data.context_label || null,
        };
      } catch (e) {
        this.detailError = 'Could not load this result.';
      } finally {
        this.detailLoading = false;
        if (category === 'questions') {
          this.scheduleCenterSearchQuestionScroller();
        } else if (category === 'mocks') {
          this.scheduleCenterSearchMockScroller();
        }
      }
    },

    pickSearchTopicLabel(q, hit, detail) {
      const src = { ...(hit || {}), ...(q || {}), ...(detail || {}) };
      const topic = this.questionScope === 'areas'
        ? this.pickAreasSearchTopicLabel(q, hit, detail)
        : (
          (src.condition && src.condition.title)
          || src.condition_title
          || src.condition_name
          || src.presentation_title
          || src.presentation_name
          || src.subject_title
          || src.subject_name
          || src.chapter_title
          || src.sublist_title
          || src.topic_title
          || src.context_label
          || null
        );
      if (!topic) return null;
      const text = String(topic).trim();
      if (!text) return null;
      if (/[>›]/.test(text)) {
        const parts = text.split(/\s*[>›]\s*/).map((s) => s.trim()).filter(Boolean);
        return parts[parts.length - 1] || text;
      }
      return text;
    },

    pickAreasSearchTopicLabel(q, hit, detail) {
      const src = { ...(hit || {}), ...(q || {}), ...(detail || {}) };
      return (
        (src.sublist && src.sublist.title)
        || src.sublist_title
        || (src.condition && src.condition.title)
        || src.condition_title
        || src.condition_name
        || (src.subject && src.subject.title)
        || src.subject_title
        || src.subject_name
        || src.chapter_title
        || src.context_label
        || null
      );
    },

    resolveAreasSectionLabel(q, hit, detail) {
      const src = { ...(hit || {}), ...(q || {}), ...(detail || {}) };
      const subject = src.subject
        || (src.condition && src.condition.subject)
        || (src.sublist && src.sublist.condition && src.sublist.condition.subject)
        || null;
      const areaId = subject && subject.area_id != null ? Number(subject.area_id) : 1;
      return areaId === 2 ? 'Areas of professional knowledge' : 'Areas of clinical practice';
    },

    buildAreasQuestionBreadcrumbItems(hit, q, detail) {
      const fromApi = detail.breadcrumb_labels || hit.breadcrumb_labels;
      if (Array.isArray(fromApi) && fromApi.length) {
        return fromApi.map((x) => String(x).trim()).filter(Boolean);
      }

      const topic = this.pickAreasSearchTopicLabel(q, hit, detail);
      const areaSection = this.resolveAreasSectionLabel(q, hit, detail);
      return ['MLA content map', 'Areas', areaSection, topic || 'Question'].filter(Boolean);
    },

    onSearchScrollerItemClick(hitIdx) {
      if (this.searchScrollerDragMoved) return;
      const hit = this.questionHits[hitIdx];
      if (!hit) return;
      this.selectHit('questions', hit);
    },

    onSearchMockScrollerItemClick(hitIdx) {
      if (this.searchScrollerDragMoved) return;
      const hit = this.mockHits[hitIdx];
      if (!hit) return;
      this.selectHit('mocks', hit);
    },

    previousQuestionHit() {
      if (!this.canGoPreviousQuestionHit || this.detailLoading) return;
      const hit = this.questionHits[this.selectedQuestionHitIndex - 1];
      if (hit) this.selectHit('questions', hit);
    },

    nextQuestionHit() {
      if (!this.canGoNextQuestionHit || this.detailLoading) return;
      const hit = this.questionHits[this.selectedQuestionHitIndex + 1];
      if (hit) this.selectHit('questions', hit);
    },

    previousNoteHit() {
      if (!this.canGoPreviousNoteHit || this.detailLoading) return;
      const hit = this.noteHits[this.selectedNoteHitIndex - 1];
      if (hit) this.selectHit('notes', hit);
    },

    nextNoteHit() {
      if (!this.canGoNextNoteHit || this.detailLoading) return;
      const hit = this.noteHits[this.selectedNoteHitIndex + 1];
      if (hit) this.selectHit('notes', hit);
    },

    previousMockHit() {
      if (!this.canGoPreviousMockHit || this.detailLoading) return;
      const hit = this.mockHits[this.selectedMockHitIndex - 1];
      if (hit) this.selectHit('mocks', hit);
    },

    nextMockHit() {
      if (!this.canGoNextMockHit || this.detailLoading) return;
      const hit = this.mockHits[this.selectedMockHitIndex + 1];
      if (hit) this.selectHit('mocks', hit);
    },

    scrollSearchQuestions(direction) {
      const el = this.$refs.searchQuestionScroll;
      if (!el) return;
      el.scrollLeft += direction * Math.max(240, Math.floor(el.clientWidth * 0.7));
    },

    scrollSearchMocks(direction) {
      const el = this.$refs.searchMockScroll;
      if (!el) return;
      el.scrollLeft += direction * Math.max(240, Math.floor(el.clientWidth * 0.7));
    },

    scheduleCenterSearchQuestionScroller() {
      const index = this.selectedQuestionHitIndex;
      if (index < 0) return;
      if (this._centerSearchScrollerTimer) {
        clearTimeout(this._centerSearchScrollerTimer);
      }
      this.$nextTick(() => {
        this._centerSearchScrollerTimer = setTimeout(() => {
          this._centerSearchScrollerTimer = null;
          this.centerSearchScroller('searchQuestionScroll', index);
        }, 40);
      });
    },

    scheduleCenterSearchMockScroller() {
      const index = this.selectedMockHitIndex;
      if (index < 0) return;
      if (this._centerSearchMockScrollerTimer) {
        clearTimeout(this._centerSearchMockScrollerTimer);
      }
      this.$nextTick(() => {
        this._centerSearchMockScrollerTimer = setTimeout(() => {
          this._centerSearchMockScrollerTimer = null;
          this.centerSearchScroller('searchMockScroll', index);
        }, 40);
      });
    },

    centerSearchQuestionScroller(index) {
      this.centerSearchScroller('searchQuestionScroll', index);
    },

    centerSearchScroller(refName, index) {
      const container = this.$refs[refName];
      if (!container || index < 0) return;

      const items = container.querySelectorAll('.search-q-number');
      const selectedItem = items[index];
      if (!selectedItem) return;

      const containerRect = container.getBoundingClientRect();
      const itemRect = selectedItem.getBoundingClientRect();
      const itemCenterInScroll =
        (itemRect.left - containerRect.left)
        + container.scrollLeft
        + (itemRect.width / 2);
      const scrollPosition = itemCenterInScroll - (container.clientWidth / 2);

      container.scrollTo({
        left: Math.max(0, scrollPosition),
        behavior: 'smooth',
      });
    },

    onSearchScrollerMouseDown(event, refName = 'searchQuestionScroll') {
      if (event.button !== 0) return;
      const container = this.$refs[refName];
      if (!container) return;
      this._activeSearchScrollerRef = refName;
      this.searchScrollerDragging = true;
      this.searchScrollerDragMoved = false;
      this.searchScrollerStartX = event.pageX;
      this.searchScrollerScrollLeft = container.scrollLeft;
      this._onSearchScrollerMove = (e) => this.onSearchScrollerMouseMove(e);
      this._onSearchScrollerUp = () => this.onSearchScrollerMouseUp();
      document.addEventListener('mousemove', this._onSearchScrollerMove);
      document.addEventListener('mouseup', this._onSearchScrollerUp);
    },

    onSearchScrollerMouseMove(event) {
      if (!this.searchScrollerDragging) return;
      const delta = event.pageX - this.searchScrollerStartX;
      if (Math.abs(delta) > 4) this.searchScrollerDragMoved = true;
      const container = this.$refs[this._activeSearchScrollerRef || 'searchQuestionScroll'];
      if (container) {
        container.scrollLeft = this.searchScrollerScrollLeft - delta;
      }
    },

    onSearchScrollerMouseUp() {
      this.searchScrollerDragging = false;
      this.teardownSearchScrollerDrag();
      setTimeout(() => {
        this.searchScrollerDragMoved = false;
      }, 0);
    },

    teardownSearchScrollerDrag() {
      if (this._onSearchScrollerMove) {
        document.removeEventListener('mousemove', this._onSearchScrollerMove);
        this._onSearchScrollerMove = null;
      }
      if (this._onSearchScrollerUp) {
        document.removeEventListener('mouseup', this._onSearchScrollerUp);
        this._onSearchScrollerUp = null;
      }
      this._activeSearchScrollerRef = null;
    },

    openQuestionInApp() {
      const q = this.detail && this.detail.question;
      if (!q) return;
      const hit = (this.selectedHit && this.selectedHit.item) || {};
      const src = { ...hit, ...q };

      let title;
      let entityId;
      if (src.sublist_id) {
        title = 'Sublist';
        entityId = src.sublist_id;
      } else if (src.condition_id) {
        title = 'Conditions';
        entityId = src.condition_id;
      } else if (this.questionScope !== 'areas' && src.presentation_id) {
        title = 'Presentations';
        entityId = src.presentation_id;
      } else if (src.subject_id) {
        title = 'Chapter';
        entityId = src.subject_id;
      } else {
        return;
      }

      const startCode = src.code || q.code || '';
      localStorage.setItem('questiontitle', title);
      if (startCode) {
        localStorage.setItem('questionStartCode', startCode);
      }
      try {
        const breadcrumbLabels = this.questionScope === 'areas'
          ? this.buildAreasQuestionBreadcrumbItems(hit, q, this.detail || {})
          : ['MLA CONTENT MAP', 'Search', title];
        localStorage.setItem(
          'questionBreadcrumbLabels',
          JSON.stringify(breadcrumbLabels)
        );
      } catch (e) { /* ignore */ }

      this.$router.push({
        name: 'QuestionsPage',
        params: { id: entityId },
        query: {
          from: this.$route.fullPath,
          ...(startCode ? { startCode } : {}),
        },
      });
    },

    openNoteInApp() {
      if (!this.selectedHit || !this.detail) return;
      const type = this.detail.record_type || this.selectedHit.item.record_type || 'notes';
      const id = this.selectedHit.item.id;
      const title = this.noteResultLabel({
        title: this.detail.title || 'Notes',
        number: this.detail.number || this.selectedHit.item.number,
      });
      localStorage.setItem('notestitle', title);
      localStorage.setItem('notestype', type === 'subnotes' ? 'subnotes' : 'notes');
      localStorage.setItem('notesid', String(id));
      localStorage.setItem('notesListRestoreSection', 'BY AREAS');
      localStorage.setItem('notesListRestoreViewMode', 'areas');
      localStorage.setItem('notesViewMode', 'areas');
      this.$router.push({
        path: '/notespage',
        query: { type: type === 'subnotes' ? 'subnotes' : 'notes', id: String(id), from: this.$route.fullPath },
      });
    },

    openMockInApp() {
      if (!this.selectedHit || !this.detail) return;
      const hit = this.selectedHit.item || {};
      const mockId = this.detail.mock_id || hit.mock_id;
      const questionId = hit.id;
      if (!mockId || !questionId) return;

      localStorage.setItem('question', 'normal');
      localStorage.setItem('mockObject', JSON.stringify({
        id: mockId,
        title: this.detail.mock_title || hit.mock_title || '',
        duration: this.detail.mock_duration || hit.mock_duration || 0,
      }));
      localStorage.setItem('mockStartQuestionId', String(questionId));
      this.$router.push({
        name: 'MockQuestionPreview',
        params: { id: mockId },
        query: { from: this.$route.fullPath },
      });
    },
  },
};
</script>

<style scoped>


::-webkit-scrollbar {
    width: 0px;
    height: 10px;
  }
  
  /* Track */
  ::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
  
  /* Handle */
 
.search-page {
  min-height: 100vh;
  background: #fff;
  text-align: left;
  overflow-x: hidden;
}

.search-shell {
  position: relative;
  min-height: calc(100vh - 140px);
  box-sizing: border-box;
}

.search-centered-status {
  margin-top: 16px;
  text-align: left;
  font-size: 15px;
  color: #666;
  font-family: Helveticacondensed, Helvetica, sans-serif;
}

.search-centered-status--error {
  color: #ED1C24;
}

/* Left search column stays docked; no center-to-left transition */
.search-shell--docked {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 0;
  padding: 0;
}

.search-shell--docked .search-sidebar {
  width: 28%;
  flex: 0 0 28%;
  max-width: 320px;
  border-right: 1px solid #d0d2d3;
  min-height: calc(100vh - 140px);
}

.search-shell--docked .search-sidebar-inner {
  width: 100%;
  margin: 0;
  padding: 24px 12px;
}

.search-shell--docked.search-shell--split .search-sidebar-inner--split {
  padding: 24px 12px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  height: calc(100vh - 140px);
  box-sizing: border-box;
}

.search-panel {
  flex: 1 1 auto;
  min-width: 0;
  min-height: calc(100vh - 140px);
}

.search-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;
  padding: 24px 20px 40px;
  min-height: calc(100vh - 140px);
}

.search-body--split {
  flex-direction: row;
  align-items: flex-start;
  gap: 0;
  padding: 0;
}

.search-sidebar--compact {
  width: 233px;
  flex: none;
}

.search-body--split .search-sidebar {
  width: 28%;
  flex: 0 0 28%;
  max-width: 320px;
  border-right: 1px solid #d0d2d3;
  min-height: calc(100vh - 140px);
}

.search-sidebar-inner {
  padding: 0;
}

.search-sidebar-inner--split {
  padding: 24px 12px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  height: calc(100vh - 140px);
  box-sizing: border-box;
}

.search-main {
  width: 75%;
  flex: 1 1 75%;
  min-width: 0;
  min-height: calc(100vh - 140px);
}

.search-results-stack {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px 28px 40px;
  box-sizing: border-box;
}

.search-controls-box {
  width: 100%;
  border: 1px solid #1A90FF;
  border-radius: 20px;
  box-shadow: 1.42px 1.42px 2.84px rgba(0, 0, 0, 0.5);
  padding: 13px 10px;
  flex-shrink: 0;
}

.search-page--notes .search-controls-box,
.search-page--notes .search-input-row input {
  border-color: #faad1b;
}

.search-page--notes .search-back-to-results:hover,
.search-page--notes .search-detail-back {
  color: #faad1b;
}

.search-page--mocks .search-controls-box,
.search-page--mocks .search-input-row input {
  border-color: #ed1c24;
}

.search-page--mocks .search-back-to-results:hover,
.search-page--mocks .search-detail-back {
  color: #ed1c24;
}

.search-input-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.search-back-to-results {
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  color: #929497;
  flex-shrink: 0;
}

.search-back-to-results:hover {
  color: #1A90FF;
}

.search-input-row input {
  flex: 1;
  border: 0.5px solid #1A90FF;
  border-radius: 20px;
  padding: 6px 10px;
  font-size: 14px;
  font-family: Helveticacondensed, Helvetica, sans-serif;
}

.search-input-row input:focus {
  outline: none;
}

.search-go-btn {
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  display: flex;
}

.search-category-checks {
  margin-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-left: 8px;
}

.search-check-row {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  font-family: Helveticacondensed, Helvetica, sans-serif;
  color: #58595B;
  position: relative;
}

.search-check-row input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.search-check-box {
  width: 16px;
  height: 16px;
  border: 1.5px solid #939598;
  border-radius: 50%;
  flex-shrink: 0;
  background: #fff;
  position: relative;
  box-sizing: border-box;
}

.search-check-box::after {
  content: '';
  position: absolute;
  left: 50%;
  top: 50%;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #c7c8ca;
  transform: translate(-50%, -50%);
  opacity: 0.55;
}

.search-check-row input:checked + .search-check-box {
  background: #fff;
  border-color: #939598;
}

.search-check-row input:checked + .search-check-box::after {
  opacity: 1;
  background: #1a90ff;
}

.search-page--notes .search-check-row input:checked + .search-check-box::after {
  background: #fbad18;
}

.search-page--mocks .search-check-row input:checked + .search-check-box::after {
  background: #ed1c24;
}

.search-check-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
  flex-shrink: 0;
}

.search-hits-panel {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.search-hits-scroll {
  overflow-y: auto;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  scrollbar-width: thin;
}

.search-results-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.search-result-block {
  border: 0.5px solid;
  border-radius: 20px;
  overflow: hidden;
}

.search-hits-scroll .search-result-block {
  border-radius: 14px;
  flex-shrink: 0;
}

.search-result-head {
  padding: 8px 12px 6px;
  box-shadow: 1.42px 1.42px 2.84px rgba(0, 0, 0, 0.5);
}

.search-hits-scroll .search-result-head {
  padding: 6px 10px;
  box-shadow: none;
}

.search-result-head h4 {
  margin: 0;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
}

.search-hits-scroll .search-result-head h4 {
  font-size: 12px;
}

.search-result-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 16px;
}

.search-hits-scroll .search-result-grid {
  padding: 10px;
}

.search-result-num {
  border: 2px solid transparent;
  box-sizing: border-box;
  padding: 0;
  border-radius: 5px;
  font-family: Helveticacondensed, Helvetica, sans-serif;
  font-size: 12px;
  width: 36px;
  height: 31px;
  line-height: 27px;
  cursor: pointer;
  color: #000;
}

.search-result-num--active {
  font-weight: 700;
}

.search-detail-panel {
  padding: 24px 28px 40px;
  overflow-y: auto;
  max-height: calc(100vh - 140px);
}

.search-detail-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 16px;
  border: none;
  background: transparent;
  color: #1A90FF;
  font-size: 14px;
  font-weight: 600;
  font-family: Helveticacondensed, Helvetica, sans-serif;
  cursor: pointer;
  padding: 4px 0;
}

.search-detail-back:hover {
  text-decoration: underline;
}

.search-question-breadcrum {
  margin: 0 0 8px;
}

.search-breadcrumb {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  font-weight: 600;
  font-size: 14px;
  padding: 4px 0 8px;
  row-gap: 6px;
}

.search-breadcrumb .breadcrumb-item {
  padding: 3px 10px;
  color: #fff;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 300;
  background: #BBBDBF;
}

.search-breadcrumb .breadcrumb-item.active {
  background-color: #1A90FF;
}

.search-page--notes .search-breadcrumb .breadcrumb-item.active,
.search-breadcrumb--notes .breadcrumb-item.active {
  background-color: #faad1b;
}

.search-page--mocks .search-breadcrumb .breadcrumb-item.active,
.search-breadcrumb--mocks .breadcrumb-item.active {
  background-color: #ED1C24;
}

.search-breadcrumb .breadcrumb-arrow {
  padding: 0 6px;
  color: #BBBDBF;
  font-size: 16px;
}

.search-question-scroller-wrap {
  margin: 0 0 12px;
}

.search-question-scroller-row {
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
}

.search-q-scroll-arrow {
  flex-shrink: 0;
  border: none;
  background: transparent;
  color: #939598;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
}

.search-question-slide {
  display: flex;
  align-items: center;
  gap: 1px;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;
  scrollbar-width: none;
  -ms-overflow-style: none;
  width: 100%;
  min-height: 72px;
  padding: 12px 0;
  cursor: grab;
  user-select: none;
}

.search-question-slide::-webkit-scrollbar {
  display: none;
}

.search-question-slide--dragging {
  cursor: grabbing;
  scroll-behavior: auto;
}

.search-q-number {
  min-width: 24px;
  height: 50px;
  flex-shrink: 0;
  border-radius: 4px;
  background: #F1F1F2;
  color: #000;
  font-family: Helveticacondensed, Helvetica, sans-serif;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-sizing: border-box;
  border: 2px solid transparent;
}

.search-q-number-label {
  pointer-events: none;
  line-height: 1;
}

.search-q-number--active {
  height: 72px;
  font-weight: 700;
  font-size: 16px;
}

.search-question-nav-arrows {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 38px;
  padding: 14px 0 0;
}

.search-nav-disabled {
  opacity: 0.4;
  pointer-events: none;
  cursor: default;
}

.search-detail-body {
  max-width: 900px;
}

.search-context-banner {
  background: #eef6ff;
  border-left: 4px solid #1A90FF;
  padding: 10px 14px;
  margin-bottom: 16px;
  font-weight: 600;
  color: #231F20;
  border-radius: 0 8px 8px 0;
}

.search-detail-title {
  font-size: 18px;
  margin: 0 0 16px;
  color: #58595B;
}

.search-q-text {
  border: 1px solid #d0d2d3;
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 16px;
  line-height: 1.5;
}

.search-q-option {
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 8px;
  display: flex;
  gap: 8px;
}

.search-open-full {
  margin-top: 16px;
  background: #1A90FF;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 10px 18px;
  cursor: pointer;
  font-weight: 600;
}

.search-open-full--notes {
  background: #F59C00;
}

.search-open-full--mocks {
  background: #ED1C24;
}

.search-rich-content {
  line-height: 1.55;
}

.search-state {
  padding: 20px;
  text-align: center;
  color: #58595B;
}

.search-state--compact {
  padding: 12px 8px;
  font-size: 13px;
}

.search-state--error {
  color: #ED1C24;
}

.search-state--hint {
  color: #929497;
}

.search-page >>> .search-hit,
.search-page ::v-deep .search-hit,
.search-page >>> mark.search-hit,
.search-page ::v-deep mark.search-hit {
  background: #ffe566;
  color: inherit;
  padding: 0 2px;
  border-radius: 2px;
}

@media (max-width: 900px) {
  .search-body--split {
    flex-direction: column;
  }

  .search-body--split .search-sidebar,
  .search-main {
    width: 100%;
    max-width: none;
    flex: none;
    min-height: auto;
  }

  .search-body--split .search-sidebar {
    border-right: none;
    border-bottom: 1px solid #d0d2d3;
  }

  .search-sidebar-inner--split {
    height: auto;
  }

  .search-hits-scroll {
    max-height: 280px;
  }

  .search-detail-panel {
    max-height: none;
  }
}
</style>
